/**
 * CompetitionView - Main Orchestration Component
 *
 * Refactored from 267 lines to ~120 lines by extracting:
 * - useCompetitionState: Local UI state management
 * - useCompetitionData: Data fetching and computed values
 * - CompetitionModals: Modal rendering
 * - CompetitionContent: Main content rendering
 *
 * This component now focuses solely on orchestration.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import AppHeader from '@/components/layout/AppHeader';
import { TopPageAd, BelowHeaderAd } from '@/components/ads/HeaderAds';
import LobbyBackBar from '@/components/layout/LobbyBackBar';
import { AppHeaderStyleProvider } from '@low6-pickem/ui';
import { isPickemHeaderAnchored, isPickemHeaderVisible } from '@low6-pickem/style-config';
import ContestDetailsModal from '@/components/modals/ContestDetailsModal';
import MarketingConsentModal from '@/components/modals/MarketingConsentModal';
import { shouldShowMarketingConsentModal } from '@/constants/marketingConsentCopy';
import { usePickemMarketingConsent, useRecordMarketingConsent } from '@/api/query/usePickem';
import { resolveMarketingConsentHeaderUrl } from '@/utils/marketingConsentHeader';
import ReserveEntryScreen from '@/components/screens/ReserveEntryScreen';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { CompetitionContent } from './CompetitionContent';
import ContestOverflowMenu from './ContestOverflowMenu';
import { CompetitionModals } from './CompetitionModals';
import { countAnsweredQuestions } from '@/utils/questions/questionProgress';
import { IPBlockedDialog } from './IPBlockedDialog';
import { useCompetitionState } from './hooks/useCompetitionState';
import { useCompetitionData } from './hooks/useCompetitionData';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setPageTargeting, clearPageTargeting } from '@/utils/gpt';
import { hasLobbyDestination, returnToLobby } from '@/utils/lobbyReturn';

interface CompetitionViewProps {
  gameId: string;
  onBack: () => void;
}

export const CompetitionView: React.FC<CompetitionViewProps> = ({ gameId, onBack }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { pickemFullNavigation } = useTenant();
  const [showContestDetails, setShowContestDetails] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const from = searchParams.get('from');

  // Edit-mode for the picks UI is owned here so the success-modal close
  // and submit-success path can deterministically drop the user back to
  // the My Picks summary view. Default behaviour:
  //   - Arriving from /main/myPicks → start in edit mode (the user clicked
  //     a tile in their picks list, presumably to edit).
  //   - Arriving with ?edit=1 → start in edit mode. This is the deep link
  //     behind every "Edit Picks" affordance, including the external lobby's
  //     (apps/lobby), so pressing Edit Picks lands on the selections rather
  //     than on a summary carrying a second Edit Picks button.
  //   - Otherwise → start on the summary view; the no-entry case still
  //     renders the picks UI via `!hasEntered || isEditing` in the child,
  //     and an entered user lands on the summary by default.
  const [isEditing, setIsEditing] = useState(
    from === 'myPicks' || searchParams.get('edit') === '1'
  );
  const [hasDismissedMarketing, setHasDismissedMarketing] = useState(false);

  useEffect(() => {
    setHasDismissedMarketing(false);
  }, [gameId]);

  // Pass the contest ID to the GPT ad stack as a page-level targeting key so
  // the ad server can target sponsored ads to a specific game/contest. Only
  // set while a specific contest is in context (this view); clear on unmount
  // so the value doesn't leak onto subsequent non-contest pages.
  useEffect(() => {
    if (!gameId) {
      return;
    }

    setPageTargeting({ s5: gameId });

    return () => {
      clearPageTargeting('s5');
    };
  }, [gameId]);

  // Local UI state (modals, leaderboard)
  const {
    modals,
    isLeaderboardOpen,
    openQuickPickModal,
    closeQuickPickModal,
    openFavPickModal,
    closeFavPickModal,
    openClearModal,
    closeClearModal,
    openSuccessModal,
    closeSuccessModal,
    openLeaderboard,
    closeLeaderboard,
  } = useCompetitionState();

  /**
   * A successful submit confirms the entry before moving the player anywhere: the
   * "Picks Submitted" popup opens over the contest's own summary, and it is the only
   * place the POST_ENTRY ad placement is served.
   *
   * Dropping out of edit mode here (rather than when the popup closes) means the summary
   * is already behind the popup, so dismissing it reveals the submitted picks.
   */
  const handleSubmitSuccess = () => {
    setIsEditing(false);
    openSuccessModal();
  };

  const canReturnToLobby = hasLobbyDestination(pickemFullNavigation, searchParams);

  /**
   * Leaving the post-entry popup, by any of its controls. Entry is finished, so closing the
   * confirmation is not a way to stay on the contest — the CTA, the X, Escape and the
   * overlay all take the player onward to the same place.
   *
   * `returnToLobby` leaves the pick'em app entirely, so nothing after it runs. Everyone
   * else stays in-app on My Picks, where their picks and the leaderboard are.
   */
  const handleExitAfterEntry = () => {
    closeSuccessModal();
    if (canReturnToLobby && returnToLobby(searchParams)) return;
    navigate('/main/myPicks');
  };

  const exitAfterEntryLabel = canReturnToLobby
    ? t('modal.picksSubmitted.returnToLobby')
    : t('modal.picksSubmitted.viewMyPicks');

  // Data fetching and computed values
  const {
    pickemDetails,
    competitionData,
    isLoading,
    error,
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleOverUnderSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    favPick,
    buildSubmissionPayload,
    submitMutation,
    hasExistingSelections,
    selectionResults,
    selectionPoints,
    timeDisplay,
    isContestClosed,
    isViewOnly,
    isResultsMode,
  } = useCompetitionData({ gameId, onSuccessSubmit: handleSubmitSuccess });

  // Action handlers
  const handleClear = () => {
    clearSelections();
    closeClearModal();
    toast({
      title: 'Your selections have been cleared!',
      className: 'bg-pickem-primary text-white border-pickem-primary',
    });
  };

  const handleQuickPick = () => {
    quickPick();
    closeQuickPickModal();
    toast({
      title: 'Quick picks selected!',
      description: 'Random selections have been made for all questions.',
    });
  };

  const handleFavPick = () => {
    favPick();
    closeFavPickModal();
    toast({
      title: 'Popular picks selected!',
      description: 'Popular selections have been made for all questions.',
    });
  };

  const handleSaveChanges = async () => {
    if (isContestClosed) {
      toast({
        title: 'Contest closed',
        description: 'This game has started and this contest is closed.',
        variant: 'destructive',
      });
      return;
    }

    const payload = buildSubmissionPayload();

    if (payload.length === 0) {
      toast({
        title: 'No selections',
        description: 'Please make at least one selection before saving.',
        variant: 'destructive',
      });
      return;
    }

    // The mutation's onSuccess (handleSubmitSuccess) owns what happens next, so a submit
    // that fails leaves the player on their picks rather than navigating them away.
    await submitMutation.mutateAsync({ selections: payload });
  };

  const headerStyleConfig = pickemDetails?.pickem?.style_config?.header as
    | Record<string, unknown>
    | undefined;

  /**
   * Artwork for the POST-entry popup, uploaded per contest on the Styling tab (Entry
   * section). Read here rather than inside the modal because `style_config` is only
   * threaded this far — the modal takes plain URLs, the same way it takes its exit label.
   *
   * The `entry` section also holds the PRE-entry confirmation copy, which
   * `CompetitionContent` reads separately; these two keys are the post-entry half of it.
   */
  const entryStyleConfig = pickemDetails?.pickem?.style_config?.entry as
    | Record<string, unknown>
    | undefined;
  const postEntryImageUrl = entryStyleConfig?.post_entry_image as string | undefined;
  const postEntryImageUrlMobile = entryStyleConfig?.post_entry_image_mobile as string | undefined;
  const showHeaderBanner = isPickemHeaderVisible(headerStyleConfig);
  // Whether the banner stays pinned while the player scrolls the questions. Anchored is
  // the default, so contests created before the toggle existed are unaffected.
  const headerAnchored = isPickemHeaderAnchored(headerStyleConfig);
  const headerBgGradientStart = headerStyleConfig?.gradient_start as string | undefined;
  const headerBgGradientEnd = headerStyleConfig?.gradient_end as string | undefined;
  const headerBgImageUrl = headerStyleConfig?.background_image as string | undefined;
  const headerBgImageUrlMobile = headerStyleConfig?.background_image_mobile as string | undefined;
  // Whether the admin configured any banner asset. When the banner is enabled
  // but nothing is configured, fall back to the full default banner (default
  // gradient + clouds + default logo); otherwise show only configured assets
  // (suppress the defaults so they don't mix/flash) — DKPLAT-461.
  const headerHasAssets = Boolean(
    String(headerStyleConfig?.logo ?? '').trim() ||
      String(headerBgImageUrl ?? '').trim() ||
      String(headerBgImageUrlMobile ?? '').trim() ||
      String(headerBgGradientStart ?? '').trim() ||
      String(headerBgGradientEnd ?? '').trim()
  );

  // Feature-gate flags from style_config. Default OFF: absent or false ⇒ hidden.
  const showPopularPicks =
    (pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined)
      ?.showPopularPicks === true;
  const showTermsCheckbox =
    (pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined)
      ?.showTermsCheckbox === true;
  // When true, the info icon becomes a three-dot overflow menu (Quick Pick /
  // Popular Pick / Clear Selection / Contest Details) and the standalone Quick
  // Pick/Clear row is dropped (DKPLAT-664). Frontend-only.
  //
  // Lives in the header section because it replaces whichever info icon is
  // currently visible: the banner's when the banner is enabled, the contest
  // card's when it is not. It is deliberately not gated on header.enabled.
  // Default ON: absent means enabled, so only an explicit `false` opts out.
  const showOverflowMenu = headerStyleConfig?.showOverflowMenu !== false;
  // When true, team/jersey artwork is served from the low-res `-low` blob
  // folders (DKPLAT-669). Frontend-only; mainly for Skypicks.
  const lowResImages =
    (pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined)?.lowResImages ===
    true;

  // Whether this contest may use licensed (IP) team artwork (DKPLAT-357). With it
  // off, NFL renders the unlicensed team jerseys instead of the paid-for logos.
  // Absent is treated as false: no declared IP rights means do not use IP.
  const leagueIp = pickemDetails?.pickem?.league_ip === true;
  const teamAssetFolder =
    typeof (pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined)
      ?.teamAssetFolder === 'string'
      ? ((pickemDetails?.pickem?.style_config as Record<string, unknown>).teamAssetFolder as string)
      : null;

  // Order is irrelevant to the count, so the questions are used unsorted.
  const hasAnyPicks =
    countAnsweredQuestions(pickemDetails?.pickem?.questions || [], selections) > 0;

  // Built once here and handed to whichever surface owns the info icon — the
  // banner when it is visible, otherwise the contest card via CompetitionContent.
  // A single construction site keeps the two placements from drifting apart.
  const overflowMenu = showOverflowMenu ? (
    <ContestOverflowMenu
      id={pickemDetails?.pickem?.pickem_id || ''}
      onQuickPick={openQuickPickModal}
      onFavouritePick={openFavPickModal}
      onClear={openClearModal}
      onInfoClick={() => setShowContestDetails(true)}
      hasAnyPicks={hasAnyPicks}
      showFavouritePick={showPopularPicks}
      hidePickActions={isViewOnly || isResultsMode}
    />
  ) : undefined;

  const styleConfig = pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined;
  const showMarketingConsentModal = shouldShowMarketingConsentModal(styleConfig);
  const { data: marketingConsentData } = usePickemMarketingConsent(
    gameId,
    showMarketingConsentModal
  );
  const recordMarketingConsent = useRecordMarketingConsent(gameId);
  const marketingConsentVersion = marketingConsentData?.consent ?? null;
  const marketingConsentConfig = styleConfig?.marketingConsent as
    | Record<string, unknown>
    | undefined;
  const hasPersistedMarketingConsent = marketingConsentData?.marketing_consent_given === true;
  const marketingModalOpen =
    showMarketingConsentModal &&
    !!marketingConsentVersion &&
    !hasPersistedMarketingConsent &&
    !hasDismissedMarketing;
  const marketingHeaderImageUrl =
    resolveMarketingConsentHeaderUrl(marketingConsentVersion) ||
    (marketingConsentConfig?.headerImageUrl as string | undefined) ||
    headerStyleConfig?.background_image ||
    pickemDetails?.pickem?.text_main_image_en ||
    null;

  const handleMarketingAccept = async () => {
    try {
      await recordMarketingConsent.mutateAsync(true);
      setHasDismissedMarketing(true);
    } catch {
      toast({
        title: 'Unable to save consent',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleMarketingClose = () => {
    setHasDismissedMarketing(true);
  };

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return <ErrorState onBack={onBack} />;
  }

  // DKPLAT-528 "Reserve a Seat": pickems without any playable questions yet
  // get the Reserve Entry screen instead of the normal picks flow. Uses the
  // real `hasQuestions` field (derived from the questions array — see
  // type-transformers.ts and useCompetitionData's fallback branch), NOT
  // `questionsPostedAt` — every existing pre-reserve-feature contest has a
  // null questions_posted_at, and every reservable contest has that date set
  // by definition, so it can't distinguish "no questions" from "has questions".
  // Once the contest is closed for entry there's nothing to reserve, so the
  // normal (view-only) flow still applies.
  //
  // Gated behind the admin opt-in flag (reserveASeatEnabled): the reserve flow
  // only shows when an admin has enabled it for this contest AND questions are
  // not yet posted AND the contest is not closed. When the flag is off, fall
  // through to the normal picks/view flow regardless of timing.
  const reserveASeatEnabled = competitionData.reserveASeatEnabled;
  const hasQuestions = competitionData.hasQuestions;
  if (reserveASeatEnabled && !hasQuestions && !isContestClosed) {
    return (
      <ReserveEntryScreen
        gameId={gameId}
        pickemDetails={pickemDetails}
        competitionData={competitionData}
        onBack={onBack}
        onNavigateUpcoming={() => navigate('/main/myPicks')}
        onNavigateLobby={onBack}
      />
    );
  }

  return (
    <AppHeaderStyleProvider
      value={{
        headerBgGradientStart,
        headerBgGradientEnd,
        headerBgImageUrl,
        headerBgImageUrlMobile,
      }}
    >
      <div
        className={`flex flex-col ${marketingModalOpen ? 'pointer-events-none select-none' : ''}`}
      >
        {/* "Back to Lobby" bar is only for the external lobby flow (full navigation
            OFF — e.g. DK via the lobby endpoint). Full-navigation tenants (SkyPicks)
            use the in-app Lobby breadcrumb instead; showing this bar there sent them
            to an external lobby URL they don't use (DKPLAT-461). */}
        {/* Top-of-page ad placement */}
        <TopPageAd />
        {!pickemFullNavigation && <LobbyBackBar />}
        {/* App Header — hidden when banner styling is disabled or empty (DKPLAT-437) */}
        {showHeaderBanner && (
          <>
            <AppHeader
              showShadow
              // Full-navigation tenants (SkyPicks) navigate back via the header arrow
              // (their in-app lobby). Non-full-nav (DK) uses the "Back to Lobby" bar
              // instead, so the header arrow stays hidden there (DKPLAT-461).
              showBackButton={pickemFullNavigation}
              onBackClick={onBack}
              onInfoClick={() => setShowContestDetails(true)}
              headerAction={overflowMenu}
              headerLogoUrl={headerStyleConfig?.logo as string | undefined}
              suppressFallbacks={headerHasAssets}
              fullNavigation={pickemFullNavigation}
              anchored={headerAnchored}
            />

            {/* Below-header ad placement */}
            <BelowHeaderAd />
          </>
        )}

        {/* Main Content */}
        <CompetitionContent
          pickemDetails={pickemDetails}
          competitionData={competitionData}
          timeDisplay={timeDisplay}
          selections={selections}
          selectionResults={selectionResults}
          selectionPoints={selectionPoints}
          currentUserId={user?.id}
          onTeamSelect={handleTeamSelect}
          onPropSelect={handlePropSelect}
          onOverUnderSelect={handleOverUnderSelect}
          onTieBreakerChange={handleTieBreakerChange}
          onQuickPick={openQuickPickModal}
          onFavouritePick={openFavPickModal}
          onClear={openClearModal}
          onSave={handleSaveChanges}
          isContestClosed={isContestClosed}
          isViewOnly={isViewOnly}
          isResultsMode={isResultsMode}
          isSaving={submitMutation.isPending}
          showLeaderBoardOnLoad={mode === 'leaderboard'}
          hasEntered={hasExistingSelections}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          onBack={onBack}
          showPopularPicks={showPopularPicks}
          lowResImages={lowResImages}
          leagueIp={leagueIp}
          teamAssetFolder={teamAssetFolder}
          showTermsCheckbox={showTermsCheckbox}
          showInfoOnGameCard={!showHeaderBanner}
          onInfoClick={() => setShowContestDetails(true)}
          // The banner already hosts the menu when it is visible, so the card
          // gets nothing — but showOverflowMenu still drops the action row,
          // since the banner menu owns Quick Pick / Clear either way.
          showOverflowMenu={showOverflowMenu}
          overflowMenu={showHeaderBanner ? undefined : overflowMenu}
        />

        {/* All Modals */}
        <CompetitionModals
          isQuickPickOpen={modals.quickPick}
          isFavPickOpen={modals.favPick}
          isClearOpen={modals.clear}
          isSuccessOpen={modals.success}
          isLeaderboardOpen={isLeaderboardOpen}
          onCloseQuickPick={closeQuickPickModal}
          onCloseFavPick={closeFavPickModal}
          onCloseClear={closeClearModal}
          onCloseLeaderboard={closeLeaderboard}
          onConfirmQuickPick={handleQuickPick}
          onConfirmFavPick={handleFavPick}
          onConfirmClear={handleClear}
          onExitAfterEntry={handleExitAfterEntry}
          exitAfterEntryLabel={exitAfterEntryLabel}
          postEntryImageUrl={postEntryImageUrl}
          postEntryImageUrlMobile={postEntryImageUrlMobile}
          gameId={gameId}
          currentUserId={user?.id}
        />

        {/* Contest Details Modal */}
        <ContestDetailsModal
          isOpen={showContestDetails}
          onClose={() => setShowContestDetails(false)}
          pickemDetails={pickemDetails}
          competitionData={competitionData}
        />

        {/* IP Blocked Dialog - Full Screen Overlay */}
        <IPBlockedDialog
          isOpen={!!submitMutation.ipBlockedError}
          errorMessage={submitMutation.ipBlockedError || undefined}
          onClose={submitMutation.clearIpBlockedError}
        />
      </div>

      <MarketingConsentModal
        isOpen={marketingModalOpen}
        onAccept={handleMarketingAccept}
        onClose={handleMarketingClose}
        headerImageUrl={marketingHeaderImageUrl}
        consentTextHtml={marketingConsentVersion?.consent_text}
        headline={marketingConsentConfig?.headline as string | undefined}
        subheadline={marketingConsentConfig?.subheadline as string | undefined}
        checkboxPrompt={marketingConsentConfig?.checkboxPrompt as string | undefined}
        eligibilityText={marketingConsentConfig?.eligibilityText as string | undefined}
        footerText={marketingConsentConfig?.footerText as string | undefined}
        checkboxTextHtml={marketingConsentVersion?.checkbox_text}
        footerTextHtml={marketingConsentVersion?.footer_text}
        buttonText={
          marketingConsentVersion?.button_text ||
          (marketingConsentConfig?.buttonText as string | undefined)
        }
      />
    </AppHeaderStyleProvider>
  );
};

export default CompetitionView;
